/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Global, tam ekran ve ürün içi sohbetlerin ortak, güvenilir asistan
 * kimliği seçicisi. Serbest metindeki `@isim` bu bileşenden seçilmedikçe
 * bir asistan kimliğine dönüştürülmez.
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { AtSign, Check, ChevronDown, Loader2, Search, Star } from 'lucide-react';

const ASSISTANT_IDENTITY_PICKER_CSS = `
.qp-assistant-identity-picker {
  position: relative;
  flex-shrink: 0;
  padding: 8px 10px 0;
  background: var(--bg-secondary);
}

.qp-assistant-identity-picker.popover-only {
  width: 100%;
  height: 0;
  padding: 0;
  background: transparent;
}

.qp-assistant-identity-trigger {
  max-width: 100%;
  min-height: 32px;
  border: 1px solid var(--border-color);
  border-radius: 999px;
  background: var(--bg-primary);
  color: var(--text-primary);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  cursor: pointer;
  font: inherit;
  font-size: 12px;
}

.qp-assistant-identity-trigger:hover:not(:disabled),
.qp-assistant-identity-trigger.active {
  border-color: var(--accent-primary);
  color: var(--accent-primary);
}

.qp-assistant-identity-trigger:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.qp-assistant-identity-trigger-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.qp-assistant-identity-popover {
  position: absolute;
  z-index: 15;
  left: 10px;
  right: 10px;
  bottom: calc(100% + 4px);
  max-height: min(360px, 55vh);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background: var(--card-bg, var(--bg-primary));
  box-shadow: var(--shadow-lg);
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.qp-assistant-identity-search {
  min-height: 36px;
  border: 1px solid var(--border-color);
  border-radius: 9px;
  background: var(--bg-secondary);
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 0 9px;
}

.qp-assistant-identity-search:focus-within {
  border-color: var(--accent-primary);
}

.qp-assistant-identity-search input {
  width: 100%;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--text-primary);
  font: inherit;
  font-size: 13px;
}

.qp-assistant-identity-list {
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.qp-assistant-identity-option {
  width: 100%;
  min-height: 52px;
  border: 1px solid transparent;
  border-radius: 9px;
  background: transparent;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px;
  text-align: left;
  cursor: pointer;
}

.qp-assistant-identity-option:hover:not(:disabled),
.qp-assistant-identity-option.active:not(:disabled),
.qp-assistant-identity-option.selected {
  border-color: var(--border-color);
  background: var(--bg-secondary);
}

.qp-assistant-identity-option:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.qp-assistant-identity-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--accent-light);
  color: var(--accent-primary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-weight: 700;
}

.qp-assistant-identity-copy {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.qp-assistant-identity-name {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 600;
}

.qp-assistant-identity-description {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-secondary);
  font-size: 11px;
}

.qp-assistant-identity-readiness {
  border-radius: 999px;
  background: var(--status-active-bg);
  color: var(--status-active);
  padding: 3px 6px;
  flex-shrink: 0;
  font-size: 10px;
}

.qp-assistant-identity-readiness.blocked {
  background: var(--action-danger-bg);
  color: var(--action-danger);
}

.qp-assistant-identity-readiness.warning,
.qp-assistant-identity-readiness.eval_passed {
  background: var(--status-draft-bg);
  color: var(--status-draft);
}

.qp-assistant-identity-state,
.qp-assistant-identity-note {
  color: var(--text-secondary);
  padding: 10px;
  font-size: 12px;
  text-align: center;
}

.qp-assistant-identity-state.error {
  color: var(--action-danger);
}

.qp-assistant-identity-note {
  border-top: 1px solid var(--border-color);
  padding: 7px 4px 1px;
  font-size: 10px;
}

.qp-assistant-identity-spinner {
  animation: qp-assistant-identity-spin 0.8s linear infinite;
  vertical-align: middle;
  margin-right: 6px;
}

@keyframes qp-assistant-identity-spin {
  to { transform: rotate(360deg); }
}
`;

export type AssistantIdentity = {
  id: number | string;
  name: string;
  description?: string | null;
  isSystem?: boolean;
  isDefault?: boolean;
  isFavorite?: boolean;
  kind?: string;
  readiness?: 'READY' | 'EVAL_PASSED' | 'WARNING' | 'BLOCKED' | string;
};

type AssistantIdentityPickerProps = {
  selected: AssistantIdentity | null;
  assistants: AssistantIdentity[];
  open: boolean;
  query: string;
  showTrigger?: boolean;
  showSearchInput?: boolean;
  navigationTargetRef?: {
    readonly current: HTMLElement | null;
  };
  loading?: boolean;
  error?: string;
  disabled?: boolean;
  language?: string;
  onOpenChange: (open: boolean) => void;
  onQueryChange: (value: string) => void;
  onSelect: (assistant: AssistantIdentity) => void;
};

type AssistantIdentityPickerLanguage =
  | 'tr'
  | 'en'
  | 'ar'
  | 'de'
  | 'fr'
  | 'es'
  | 'it'
  | 'ru'
  | 'zh'
  | 'ja';

type AssistantIdentityPickerCopy = {
  pickerLabel: string;
  emptyLabel: string;
  blockedLabel: string;
  warningLabel: string;
  evalPassedLabel: string;
  readyLabel: string;
  globalAssistant: string;
  searchPlaceholder: string;
  loadingLabel: string;
  generalAssistant: string;
  trustedSelectionNote: string;
};

const ASSISTANT_IDENTITY_PICKER_COPY: Record<
  AssistantIdentityPickerLanguage,
  AssistantIdentityPickerCopy
> = {
  tr: {
    pickerLabel: 'Asistan seç',
    emptyLabel: 'Erişebildiğiniz bir asistan bulunamadı.',
    blockedLabel: 'Hazır değil',
    warningLabel: 'Uyarılı',
    evalPassedLabel: 'Güven testi bekliyor',
    readyLabel: 'Hazır',
    globalAssistant: 'QubePro Global Asistan',
    searchPlaceholder: 'Asistan ara',
    loadingLabel: 'Asistanlar alınıyor…',
    generalAssistant: 'Genel yardımcı',
    trustedSelectionNote: 'Yalnız bu listeden yaptığınız seçim geçerlidir.',
  },
  en: {
    pickerLabel: 'Choose assistant',
    emptyLabel: 'No accessible assistant found.',
    blockedLabel: 'Not ready',
    warningLabel: 'Warning',
    evalPassedLabel: 'Trust test pending',
    readyLabel: 'Ready',
    globalAssistant: 'QubePro Global Assistant',
    searchPlaceholder: 'Search assistants',
    loadingLabel: 'Loading assistants…',
    generalAssistant: 'General assistant',
    trustedSelectionNote: 'Only a selection made from this list is trusted.',
  },
  ar: {
    pickerLabel: 'اختيار مساعد',
    emptyLabel: 'لم يتم العثور على مساعد يمكنك الوصول إليه.',
    blockedLabel: 'غير جاهز',
    warningLabel: 'تحذير',
    evalPassedLabel: 'اختبار الثقة معلّق',
    readyLabel: 'جاهز',
    globalAssistant: 'مساعد QubePro العام',
    searchPlaceholder: 'البحث عن مساعدين',
    loadingLabel: 'جارٍ تحميل المساعدين…',
    generalAssistant: 'مساعد عام',
    trustedSelectionNote: 'لا يُعتمد إلا الاختيار الذي تجريه من هذه القائمة.',
  },
  de: {
    pickerLabel: 'Assistent auswählen',
    emptyLabel: 'Kein zugänglicher Assistent gefunden.',
    blockedLabel: 'Nicht bereit',
    warningLabel: 'Warnung',
    evalPassedLabel: 'Vertrauensprüfung ausstehend',
    readyLabel: 'Bereit',
    globalAssistant: 'Globaler QubePro-Assistent',
    searchPlaceholder: 'Assistenten suchen',
    loadingLabel: 'Assistenten werden geladen…',
    generalAssistant: 'Allgemeiner Assistent',
    trustedSelectionNote: 'Nur eine Auswahl aus dieser Liste gilt als vertrauenswürdig.',
  },
  fr: {
    pickerLabel: 'Choisir un assistant',
    emptyLabel: 'Aucun assistant accessible trouvé.',
    blockedLabel: 'Non prêt',
    warningLabel: 'Avertissement',
    evalPassedLabel: 'Test de confiance en attente',
    readyLabel: 'Prêt',
    globalAssistant: 'Assistant global QubePro',
    searchPlaceholder: 'Rechercher des assistants',
    loadingLabel: 'Chargement des assistants…',
    generalAssistant: 'Assistant général',
    trustedSelectionNote: 'Seule une sélection effectuée dans cette liste est approuvée.',
  },
  es: {
    pickerLabel: 'Elegir asistente',
    emptyLabel: 'No se encontró ningún asistente accesible.',
    blockedLabel: 'No listo',
    warningLabel: 'Advertencia',
    evalPassedLabel: 'Prueba de confianza pendiente',
    readyLabel: 'Listo',
    globalAssistant: 'Asistente global de QubePro',
    searchPlaceholder: 'Buscar asistentes',
    loadingLabel: 'Cargando asistentes…',
    generalAssistant: 'Asistente general',
    trustedSelectionNote: 'Solo se considera fiable una selección hecha en esta lista.',
  },
  it: {
    pickerLabel: 'Scegli assistente',
    emptyLabel: 'Nessun assistente accessibile trovato.',
    blockedLabel: 'Non pronto',
    warningLabel: 'Avviso',
    evalPassedLabel: 'Test di affidabilità in attesa',
    readyLabel: 'Pronto',
    globalAssistant: 'Assistente globale QubePro',
    searchPlaceholder: 'Cerca assistenti',
    loadingLabel: 'Caricamento assistenti…',
    generalAssistant: 'Assistente generale',
    trustedSelectionNote: 'È considerata attendibile solo una selezione effettuata da questo elenco.',
  },
  ru: {
    pickerLabel: 'Выбрать ассистента',
    emptyLabel: 'Доступные ассистенты не найдены.',
    blockedLabel: 'Не готов',
    warningLabel: 'Предупреждение',
    evalPassedLabel: 'Ожидает проверки доверия',
    readyLabel: 'Готов',
    globalAssistant: 'Глобальный ассистент QubePro',
    searchPlaceholder: 'Поиск ассистентов',
    loadingLabel: 'Загрузка ассистентов…',
    generalAssistant: 'Общий ассистент',
    trustedSelectionNote: 'Доверенным считается только выбор из этого списка.',
  },
  zh: {
    pickerLabel: '选择助手',
    emptyLabel: '未找到可访问的助手。',
    blockedLabel: '未就绪',
    warningLabel: '警告',
    evalPassedLabel: '等待可信测试',
    readyLabel: '就绪',
    globalAssistant: 'QubePro 全局助手',
    searchPlaceholder: '搜索助手',
    loadingLabel: '正在加载助手…',
    generalAssistant: '通用助手',
    trustedSelectionNote: '仅信任从此列表中作出的选择。',
  },
  ja: {
    pickerLabel: 'アシスタントを選択',
    emptyLabel: 'アクセス可能なアシスタントが見つかりません。',
    blockedLabel: '準備未完了',
    warningLabel: '警告',
    evalPassedLabel: '信頼テスト待ち',
    readyLabel: '準備完了',
    globalAssistant: 'QubePro グローバルアシスタント',
    searchPlaceholder: 'アシスタントを検索',
    loadingLabel: 'アシスタントを読み込み中…',
    generalAssistant: '一般アシスタント',
    trustedSelectionNote: 'この一覧から選択したアシスタントのみが信頼されます。',
  },
};

function getAssistantIdentityPickerCopy(language?: string): AssistantIdentityPickerCopy {
  const normalized = String(language || '').trim().toLowerCase().split('-')[0];
  if (normalized in ASSISTANT_IDENTITY_PICKER_COPY) {
    return ASSISTANT_IDENTITY_PICKER_COPY[normalized as AssistantIdentityPickerLanguage];
  }
  return language ? ASSISTANT_IDENTITY_PICKER_COPY.en : ASSISTANT_IDENTITY_PICKER_COPY.tr;
}

function readinessLabel(
  readiness: AssistantIdentity['readiness'],
  copy: AssistantIdentityPickerCopy,
): string {
  if (readiness === 'BLOCKED') return copy.blockedLabel;
  if (readiness === 'WARNING') return copy.warningLabel;
  if (readiness === 'EVAL_PASSED') return copy.evalPassedLabel;
  return copy.readyLabel;
}

export function AssistantIdentityPicker({
  selected,
  assistants,
  open,
  query,
  showTrigger = true,
  showSearchInput = true,
  navigationTargetRef,
  loading = false,
  error = '',
  disabled = false,
  language,
  onOpenChange,
  onQueryChange,
  onSelect,
}: AssistantIdentityPickerProps) {
  const copy = getAssistantIdentityPickerCopy(language);
  const pickerLabel = copy.pickerLabel;
  const emptyLabel = copy.emptyLabel;
  const listboxId = useId();
  const listRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(-1);

  const isBlocked = useCallback(
    (index: number) => assistants[index]?.readiness === 'BLOCKED',
    [assistants],
  );
  const findEnabledIndex = useCallback((start: number, direction: 1 | -1): number => {
    if (assistants.length === 0) return -1;
    for (let offset = 0; offset < assistants.length; offset += 1) {
      const candidate = (start + (offset * direction) + assistants.length) % assistants.length;
      if (!isBlocked(candidate)) return candidate;
    }
    return -1;
  }, [assistants, isBlocked]);

  useEffect(() => {
    if (!open || loading || Boolean(error)) {
      setActiveIndex(-1);
      return;
    }
    const selectedIndex = assistants.findIndex(
      (assistant) => String(assistant.id) === String(selected?.id ?? ''),
    );
    setActiveIndex(
      selectedIndex >= 0 && !isBlocked(selectedIndex)
        ? selectedIndex
        : findEnabledIndex(0, 1),
    );
  }, [assistants, error, findEnabledIndex, isBlocked, loading, open, selected?.id]);

  useEffect(() => {
    if (!open || activeIndex < 0) return;
    listRef.current
      ?.querySelector<HTMLElement>(`[data-assistant-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  const handlePickerKeyDown = useCallback((
    event: KeyboardEvent | ReactKeyboardEvent<HTMLInputElement>,
  ) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', 'Escape'].includes(event.key)) return;
    event.preventDefault();
    event.stopPropagation();

    if (event.key === 'Escape') {
      onOpenChange(false);
      return;
    }

    if (loading || error) return;

    if (event.key === 'Enter') {
      const assistant = assistants[activeIndex];
      if (assistant && assistant.readiness !== 'BLOCKED') onSelect(assistant);
      return;
    }

    if (event.key === 'Home') {
      setActiveIndex(findEnabledIndex(0, 1));
      return;
    }
    if (event.key === 'End') {
      setActiveIndex(findEnabledIndex(assistants.length - 1, -1));
      return;
    }

    const direction = event.key === 'ArrowDown' ? 1 : -1;
    const start = activeIndex < 0
      ? (direction === 1 ? 0 : assistants.length - 1)
      : (activeIndex + direction + assistants.length) % assistants.length;
    setActiveIndex(findEnabledIndex(start, direction));
  }, [activeIndex, assistants, error, findEnabledIndex, loading, onOpenChange, onSelect]);

  useEffect(() => {
    if (!open || showSearchInput) return undefined;
    const navigationTarget = navigationTargetRef?.current;
    if (!navigationTarget) return undefined;
    const handleExternalKeyDown = (event: KeyboardEvent) => handlePickerKeyDown(event);
    navigationTarget.addEventListener('keydown', handleExternalKeyDown);
    return () => navigationTarget.removeEventListener('keydown', handleExternalKeyDown);
  }, [handlePickerKeyDown, navigationTargetRef, open, showSearchInput]);

  return (
    <>
      <style>{ASSISTANT_IDENTITY_PICKER_CSS}</style>
      <div className={`qp-assistant-identity-picker ${showTrigger ? '' : 'popover-only'}`}>
      {showTrigger ? (
        <button
          type="button"
          className={`qp-assistant-identity-trigger ${open ? 'active' : ''}`}
          onClick={() => onOpenChange(!open)}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          title={pickerLabel}
        >
          <AtSign size={15} aria-hidden />
          <span className="qp-assistant-identity-trigger-name">
            {selected?.name || copy.globalAssistant}
          </span>
          {selected?.isFavorite ? <Star size={13} fill="currentColor" aria-hidden /> : null}
          <ChevronDown size={14} aria-hidden />
        </button>
      ) : null}

      {open ? (
        <div className="qp-assistant-identity-popover">
          {showSearchInput ? (
            <label className="qp-assistant-identity-search">
              <Search size={15} aria-hidden />
              <input
                value={query}
                onChange={(event) => onQueryChange(event.target.value)}
                onKeyDown={handlePickerKeyDown}
                placeholder={copy.searchPlaceholder}
                autoFocus
                aria-label={copy.searchPlaceholder}
                aria-controls={listboxId}
                aria-activedescendant={activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined}
              />
            </label>
          ) : null}
          <div
            ref={listRef}
            id={listboxId}
            className="qp-assistant-identity-list"
            role="listbox"
            aria-label={pickerLabel}
            aria-activedescendant={activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined}
          >
            {loading ? (
              <div className="qp-assistant-identity-state">
                <Loader2 className="qp-assistant-identity-spinner" size={17} aria-hidden />
                <span>{copy.loadingLabel}</span>
              </div>
            ) : error ? (
              <div className="qp-assistant-identity-state error">{error}</div>
            ) : assistants.length === 0 ? (
              <div className="qp-assistant-identity-state">{emptyLabel}</div>
            ) : assistants.map((assistant, index) => {
              const isSelected = String(selected?.id ?? '') === String(assistant.id);
              const isActive = index === activeIndex;
              const blocked = assistant.readiness === 'BLOCKED';
              return (
                <button
                  key={String(assistant.id)}
                  id={`${listboxId}-option-${index}`}
                  data-assistant-index={index}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`qp-assistant-identity-option ${isActive ? 'active' : ''} ${isSelected ? 'selected' : ''}`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => onSelect(assistant)}
                  disabled={blocked}
                  title={blocked ? readinessLabel(assistant.readiness, copy) : assistant.description || assistant.name}
                >
                  <span className="qp-assistant-identity-avatar">@</span>
                  <span className="qp-assistant-identity-copy">
                    <span className="qp-assistant-identity-name">
                      {assistant.name}
                      {assistant.isFavorite ? <Star size={12} fill="currentColor" aria-hidden /> : null}
                    </span>
                    <span className="qp-assistant-identity-description">
                      {assistant.description || (assistant.isSystem ? copy.generalAssistant : readinessLabel(assistant.readiness, copy))}
                    </span>
                  </span>
                  <span className={`qp-assistant-identity-readiness ${String(assistant.readiness || 'READY').toLowerCase()}`}>
                    {readinessLabel(assistant.readiness, copy)}
                  </span>
                  {isSelected ? <Check size={16} aria-hidden /> : null}
                </button>
              );
            })}
          </div>
          <div className="qp-assistant-identity-note">
            {copy.trustedSelectionNote}
          </div>
        </div>
      ) : null}
      </div>
    </>
  );
}

export default AssistantIdentityPicker;
